/**
 * Black Matte VTC - Partner Auth Context
 * Manages partner authentication state
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { partnerService } from '../services/partnerService';

const PartnerAuthContext = createContext(null);

export const usePartnerAuth = () => {
  const context = useContext(PartnerAuthContext);
  if (!context) {
    throw new Error('usePartnerAuth must be used within PartnerAuthProvider');
  }
  return context;
};

export const PartnerAuthProvider = ({ children }) => {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  const initAuth = useCallback(async () => {
    const token = partnerService.getToken();
    
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await partnerService.getProfile();
      setPartner(response.partner);
      setError(null);
    } catch (err) {
      console.error('Partner auth init failed:', err);
      partnerService.clearToken();
      setPartner(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Login
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await partnerService.login(email, password);
      setPartner(response.partner);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = useCallback(() => {
    partnerService.logout();
    setPartner(null);
    setError(null);
  }, []);

  // Refresh profile
  const refreshProfile = async () => {
    try {
      const response = await partnerService.getProfile();
      setPartner(response.partner);
    } catch (err) {
      console.error('Failed to refresh profile:', err);
    }
  };

  const value = {
    partner,
    loading,
    error,
    isAuthenticated: !!partner,
    login,
    logout,
    refreshProfile,
  };

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
};

export default PartnerAuthProvider;
